import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, Spinner, Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    canConfirmSuggestedFixes,
    type SuggestedFixesState,
} from '../../suggestedFixes';
import { SuggestedFixesSummaryBody } from './SuggestedFixesSummaryBody';

/** A footer button: full width on a phone, its own width from sm up. */
const FOOTER_BUTTON = 'w-full sm:w-auto';

type SuggestedFixesDialogProps = {
    state: SuggestedFixesState;
    /** The confirmed import is on its way: Confirm spins, nothing else is pressed. */
    isImporting: boolean;
    /** Says that the whole import can be undone on Result (the wizard). */
    showUndoNote: boolean;
    onConfirm: () => void;
    /** "Review rows first", and the dialog dismissed. */
    onReview: () => void;
    /** Try again after a check that did not answer. */
    onRetry: () => void;
};

/**
 * "Import with suggested fixes?" (bos-app#213, US-10, AC-08): what the
 * suggested fixes change and what the import would write, before anything
 * is written.
 *
 * - **Applying** (`applying`): a spinner and "Applying the suggested
 *   fixes…" while the defaults are applied and checked; nothing can be
 *   pressed or dismissed.
 * - **The summary** (`ready`): how many rows are imported, then "What we
 *   changed", one line per kind with its count and how (a HeroUI `Card`,
 *   the lines between `Separator`s), the rows imported as drafts (or that
 *   none is), the rows left out for a number cell that could not be read
 *   (when any is), the header problems that refuse the import (a danger alert;
 *   Confirm then waits), the undo note in the wizard, and why a confirmed
 *   import did not go through. **Review rows first** (tertiary) and
 *   **Import N rows** (primary, spinning while the import runs).
 * - **The check did not answer** (`failed`): a danger alert, Review rows
 *   first and **Try again**.
 *
 * Dismissing the dialog (Escape, the backdrop) is Review rows first, and
 * waits as it does while the check or the import runs.
 */
export const SuggestedFixesDialog: React.FC<SuggestedFixesDialogProps> = ({
    state,
    isImporting,
    showUndoNote,
    onConfirm,
    onReview,
    onRetry,
}) => {
    const { t } = useTranslation('pages');
    const isApplying = state.status === 'applying';
    const canReview = !isApplying && !isImporting;

    return (
        <Modal
            isOpen={state.status !== 'closed'}
            onOpenChange={(open) => {
                if (!open && canReview) {
                    onReview();
                }
            }}
        >
            <Modal.Backdrop isDismissable={canReview}>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp(
                            'catalog-import-suggested-fixes-dialog',
                        )}
                    >
                        <Modal.Header>
                            <Modal.Heading>
                                {t(
                                    'catalogImport.products.suggestedFixes.dialog.title',
                                )}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4">
                            {state.status === 'applying' ? (
                                <div
                                    aria-live="polite"
                                    className="flex items-center gap-3"
                                    role="status"
                                    {...getNodeTestIdProp(
                                        'catalog-import-suggested-fixes-applying',
                                    )}
                                >
                                    <Spinner
                                        aria-hidden
                                        color="current"
                                        size="sm"
                                    />
                                    <Typography type="body-sm">
                                        {t(
                                            'catalogImport.products.suggestedFixes.applying',
                                        )}
                                    </Typography>
                                </div>
                            ) : null}
                            {state.status === 'failed' ? (
                                <Alert
                                    status="danger"
                                    {...getNodeTestIdProp(
                                        'catalog-import-suggested-fixes-failed',
                                    )}
                                >
                                    <Alert.Indicator />
                                    <Alert.Content>
                                        <Alert.Title>
                                            {t(
                                                'catalogImport.products.suggestedFixes.dialog.failedTitle',
                                            )}
                                        </Alert.Title>
                                        <Alert.Description>
                                            {t(
                                                'catalogImport.products.suggestedFixes.dialog.failed',
                                            )}
                                        </Alert.Description>
                                    </Alert.Content>
                                </Alert>
                            ) : null}
                            {state.status === 'ready' ? (
                                <SuggestedFixesSummaryBody
                                    showUndoNote={showUndoNote}
                                    submit={state.submit}
                                    summary={state.summary}
                                />
                            ) : null}
                        </Modal.Body>
                        {/* A phone stacks the buttons, the action on top, each
                            full width: two Georgian labels do not fit one row
                            (as WorkConfirmDialog does). */}
                        <Modal.Footer className="flex-col-reverse items-stretch sm:flex-row sm:items-center">
                            <Button
                                className={FOOTER_BUTTON}
                                isDisabled={!canReview}
                                onPress={onReview}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'catalog-import-suggested-fixes-review',
                                )}
                            >
                                {t(
                                    'catalogImport.products.suggestedFixes.dialog.review',
                                )}
                            </Button>
                            {state.status === 'failed' ? (
                                <Button
                                    className={FOOTER_BUTTON}
                                    onPress={onRetry}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'catalog-import-suggested-fixes-retry',
                                    )}
                                >
                                    {t(
                                        'catalogImport.products.suggestedFixes.dialog.retry',
                                    )}
                                </Button>
                            ) : null}
                            {state.status === 'ready' ? (
                                <Button
                                    className={FOOTER_BUTTON}
                                    isDisabled={
                                        !canConfirmSuggestedFixes(state.summary)
                                    }
                                    isPending={isImporting}
                                    onPress={onConfirm}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'catalog-import-suggested-fixes-confirm',
                                    )}
                                >
                                    {t(
                                        'catalogImport.products.suggestedFixes.dialog.confirm',
                                        {
                                            count: state.summary.rows,
                                            rows: countParam(
                                                state.summary.rows,
                                            ),
                                        },
                                    )}
                                </Button>
                            ) : null}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
